import PhoneInput, { CountryData } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import styled from "styled-components";
import { Typography } from "../typography";
import { Icon } from "../icons";
import * as CONSTANTS from "../../shared/constants";
import { Theme } from "../../shared/types";

const ReactPhoneInput = (
  "default" in PhoneInput ? PhoneInput.default : PhoneInput
) as typeof PhoneInput;

export type PhoneFieldChangeValue = {
  value: string;
  countryData: CountryData;
  event: React.ChangeEvent<HTMLInputElement>;
};

type PhoneFieldProps = {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: PhoneFieldChangeValue) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  initialCountry?: string | number;
  errorMessage?: string;
  enableSearch?: boolean;
  localization?: object;
  width?: "default" | "full";
} & (
  | { sensitive?: false; isDemo?: boolean }
  | { sensitive: true; isDemo: boolean }
);

export const PhoneField = (props: PhoneFieldProps) => {
  const shouldMask = props.sensitive && props.isDemo && props.value;
  const theme: Theme = CONSTANTS.defaultTheme;

  const onChange = (
    value: string,
    countryData: CountryData,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const localValue = value && value.startsWith("+") ? value : `+${value}`;
    if (props.onChange)
      props.onChange({
        value: localValue,
        countryData,
        event,
      });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {!!props.label && (
        <Typography
          style={{ marginBottom: "6px", color: theme.COLORS.GRAY_DARK }}
          variant="body-x-small"
        >
          {props.label}
          {props.required ? "*" : ""}
        </Typography>
      )}
      {shouldMask ? (
        <MaskedInput $width={props.width === "full" ? "100%" : undefined}>
          {CONSTANTS.DEMO}
        </MaskedInput>
      ) : (
        <PhoneInputWrapper
          $error={!!props.error}
          $width={props.width === "full" ? "100%" : undefined}
        >
          <ReactPhoneInput
            disabled={props.disabled}
            enableSearch={props.enableSearch}
            country={props.initialCountry}
            value={props.value ?? ""}
            placeholder={props.placeholder}
            autoFormat={false}
            countryCodeEditable={false}
            onChange={onChange}
            localization={props.localization}
          />
        </PhoneInputWrapper>
      )}
      {props.error && !!props.errorMessage && (
        <ErrorContainer>
          <ErrorIcon />
          <Typography variant="body-x-small" color="ERROR">
            {props.errorMessage}
          </Typography>
        </ErrorContainer>
      )}
    </div>
  );
};

const PhoneInputWrapper = styled.div<{ $error: boolean; $width?: string }>`
  .react-tel-input {
    font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};

    .form-control {
      font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
      border: 1px solid
        ${({ $error, theme }) =>
          $error ? theme.COLORS.ERROR : theme.COLORS.GRAY_MEDIUM};
      border-radius: 4px;
      width: ${({ $width }) => $width || "304px"};
      height: 48px;
      font-size: ${({ theme }) => theme.FONT.SIZE.BODY};
      line-height: 22px;
      color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
      padding: 13px 16px 13px 68px;
      outline: none;
      background-color: transparent;

      &:focus {
        border-color: ${({ $error, theme }) =>
          $error ? theme.COLORS.ERROR : theme.COLORS.GRAY};
        box-shadow: none;
      }

      &:disabled {
        background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
      }

      &::placeholder {
        color: ${({ theme }) => theme.COLORS.GRAY};
        opacity: 1;
      }
    }

    .flag-dropdown {
      border: none;
      background-color: transparent;
      border-radius: 4px 0 0 4px;

      &:hover,
      &:focus,
      &.open {
        background-color: transparent;
      }

      .selected-flag {
        background-color: transparent;
        padding: 0 16px 0 16px;
        width: 60px;
        border-right: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM} !important;

        &:hover,
        &:focus {
          background-color: transparent;
        }

        .arrow {
          border-top-color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
        }
      }
    }

    .country-list {
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      margin-top: 4px;

      .country {
        font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
        padding: 8px 12px;

        &:hover,
        &.highlight {
          background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
        }
      }

      .search {
        padding: 8px;

        .search-box {
          font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
          border: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
          border-radius: 4px;
          padding: 8px;
          width: 100%;
          margin: 0;

          &:focus {
            border-color: ${({ theme }) => theme.COLORS.GRAY};
          }
        }
      }
    }
  }
`;

const MaskedInput = styled.div<{ $width?: string }>`
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  border: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
  border-radius: 4px;
  width: ${({ $width }) => $width || "304px"};
  height: 48px;
  font-size: ${({ theme }) => theme.FONT.SIZE.BODY};
  line-height: 22px;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
  padding: 13px 16px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${CONSTANTS.defaultTheme.SPACING.XS};
  margin-top: 4px;
`;

const ErrorIcon = styled(Icon.Info)`
  transform: rotate(180deg);
  flex-shrink: 0;
  width: 16px;
  color: ${({ theme }) => theme.COLORS.ERROR};
`;
